/**
 * Dental Stage 1: Discharge Information — interactive form for populating fields inline.
 */
import { Plus, X } from 'lucide-react'

import { SNOOTS_BG } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import { Input } from '@/frontend/components/ui/input'
import { Label } from '@/frontend/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'
import { ProductSearch } from '@/frontend/features/inventory/product-search'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import {
  type DentalDischargeStage1Fields,
  DentalDischargeStage1Pdf,
  defaultDentalDischargeStage1Fields,
  EXTRACTION_SCHEDULING,
  feedingText,
  followUpText,
  noExtractionsText,
  procedureText,
  requiresExtractionsText,
} from '@/frontend/features/printing/documents/dental-discharge-stage1.print'
import {
  dentalClosingText,
  MEDICATION_SIDE_EFFECTS,
  medicationText,
  OUT_OF_HOURS,
  restText,
  THINGS_TO_NOTICE,
} from '@/frontend/features/printing/documents/discharge/shared'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function DentalDischargeStage1Form({
  context,
  value,
  onChange,
}: DocumentFormProps<DentalDischargeStage1Fields>) {
  const { patient, clinic } = context
  const name = patient.name

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 font-semibold">Dental Stage 1: Discharge Information</h1>

      <p>
        <span className="font-semibold">Procedure: </span>
        {procedureText(name)}
      </p>

      <h2 className="text-headline-md">Rest and activity:</h2>
      <p className="mt-1">{restText(name)}</p>

      <h2 className="text-headline-md">Feeding:</h2>
      <p className="mt-1">{feedingText(name)}</p>

      <h2 className="text-headline-md">Medication:</h2>
      <p className="mt-1">{medicationText(name)}</p>

      <div className="mt-3">
        <RadioGroup
          value={value.hasMedication}
          onValueChange={(v) => onChange({ ...value, hasMedication: v as 'none' | 'has_meds' })}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="none" id="med-none" />
            <Label htmlFor="med-none" className="font-normal">
              {name} does not have any medication to go home
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="has_meds" id="med-has" />
            <Label htmlFor="med-has" className="font-normal">
              {name} has medication to go home and are as follows:
            </Label>
          </div>
        </RadioGroup>

        {value.hasMedication === 'has_meds' && (
          <div className="mt-2 space-y-2">
            {value.medications.map((med, i) => (
              <div key={med.key} className="flex gap-2 rounded border p-2">
                <div className="flex-1 space-y-2">
                  <ProductSearch
                    onChange={(p) => {
                      const updated = [...value.medications]
                      updated[i] = { ...updated[i], productId: p?.id ?? '', name: p?.name ?? '' }
                      onChange({ ...value, medications: updated })
                    }}
                    type={['medication']}
                    placeholder="Search medication..."
                    showClear
                  />
                  <Input
                    placeholder="Dosage instructions"
                    value={med.instructions}
                    onChange={(e) => {
                      const updated = [...value.medications]
                      updated[i] = { ...updated[i], instructions: e.target.value }
                      onChange({ ...value, medications: updated })
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  className="self-start"
                  onClick={() => {
                    const updated = value.medications.filter((_, j) => j !== i)
                    onChange({ ...value, medications: updated })
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() =>
                onChange({
                  ...value,
                  medications: [
                    ...value.medications,
                    { key: crypto.randomUUID(), productId: '', name: '', instructions: '' },
                  ],
                })
              }
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add medication
            </Button>
          </div>
        )}
      </div>

      <p className="mt-2">{MEDICATION_SIDE_EFFECTS}</p>

      <h2 className="text-headline-md">Follow up appointments:</h2>
      <div className="mt-2">
        <RadioGroup
          value={value.extractionAssessment}
          onValueChange={(v) =>
            onChange({ ...value, extractionAssessment: v as 'none' | 'requires' })
          }
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="none" id="extract-none" />
            <Label htmlFor="extract-none" className="font-normal">
              {noExtractionsText(name)}
            </Label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <RadioGroupItem value="requires" id="extract-requires" />
            <Label htmlFor="extract-requires" className="font-normal">
              {requiresExtractionsText(name)}
            </Label>
            {value.extractionAssessment === 'requires' && (
              <Input
                className="inline-flex w-48"
                placeholder="number/description"
                value={value.extractionsDescription}
                onChange={(e) => onChange({ ...value, extractionsDescription: e.target.value })}
              />
            )}
            <span>extraction(s).</span>
          </div>
        </RadioGroup>
      </div>

      {value.extractionAssessment === 'requires' && <p className="mt-2">{EXTRACTION_SCHEDULING}</p>}
      <p className="mt-2">{followUpText(name)}</p>

      <h2 className="text-headline-md">Other things you may notice:</h2>
      <ul className="mt-1 list-disc pl-6">
        {THINGS_TO_NOTICE.map((item) => (
          <li key={item.slice(0, 30)} className="mt-1">
            {item}
          </li>
        ))}
      </ul>

      <h2 className="text-headline-md">Out of hours contact:</h2>
      <p className="mt-1">{OUT_OF_HOURS}</p>

      <p className="mt-4">{dentalClosingText(name)}</p>
      <p className="mt-2">Snoots Vet, North London.</p>
    </DocumentPreview>
  )
}

export const dentalDischargeStage1 = withDocumentEditor({
  id: 'dental-discharge-stage1',
  label: 'Dental Discharge — Stage 1',
  // Dental procedures are carried out at the Bounds Green clinic.
  clinic: SNOOTS_BG,
  defaultFields: defaultDentalDischargeStage1Fields,
  Preview: DentalDischargeStage1Form,
  Pdf: DentalDischargeStage1Pdf,
})
